import { Client } from "@microsoft/microsoft-graph-client";
import {
  AadUserConversationMember,
  Person,
} from "@microsoft/microsoft-graph-types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GraphAuthProvider } from "../components/graph/graphAuthProvider";
import { StateIndicator } from "../models/shared/Enums/StateIndicator";
import { Constant, GraphConstants } from "../utils/constant";
import Extensions from "../utils/extensions";
import { getContextAsync } from "./auth-slice";
import { RootState } from "./store";
import { appInsights} from "../config/app-insights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

export const initializeGraphClientAsync = createAsyncThunk(
  "graph/initializeGraphClient",
  async (graphToken: string, thunkAPI) => {
    try {
      var clientOptions = {
        authProvider: new GraphAuthProvider(graphToken),
      };
      // Initialize the graph client
      return await Client.initWithMiddleware(clientOptions);
    } catch (error :any) {
      appInsights.trackException({ exception:{ message: error, name: `${GraphSlice.name}/${initializeGraphClientAsync.name}: Exception` }, severityLevel: SeverityLevel.Error});
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getMembersAsync = createAsyncThunk(
  "graph/getMembers",
  async (graphClient: Client, thunkAPI) => {
    try {
      var response: any = [];
      const ctx = await thunkAPI.dispatch(getContextAsync()).unwrap();
      appInsights.trackTrace({ message:"team context", severityLevel: SeverityLevel.Information, properties: { ctx }})
      if (ctx === undefined) {
        appInsights.trackException({ exception:{ message:"undefined team context", name: `${GraphSlice.name}/${getMembersAsync.name}: Exception` }, severityLevel: SeverityLevel.Error});
        return thunkAPI.rejectWithValue("graph: failed to load context");
      }
      if(ctx.channel === undefined && ctx.team === undefined && ctx.chat === undefined)
      {
        response = await graphClient
        .api(GraphConstants.PeopleUrlEndpoint)
        .get();
       let message: Person[] = response.value;
       return message.flatMap((member) => member.scoredEmailAddresses!.map((address) => address.address!));
      }
      else 
      {
        var memberEmails:string[];
        if (ctx.chat !== undefined)  {
          //group chat
          response = await graphClient
            .api(
              Extensions.FormatString(
                GraphConstants.GroupChatMembersUrlEndpoint,
                ctx.chat?.id!
              )
            )
            .get();
        } else if (ctx.channel !== undefined) {
          //teams channel
          response = await graphClient
            .api(
              Extensions.FormatString(
                GraphConstants.TeamMembersUrlEndpoint,
                ctx.team?.groupId!
              )
            )
            .get();
        }
        let message: AadUserConversationMember[] = response.value;
        memberEmails= message.flatMap(member=>member.email??"")


        if(memberEmails.length > Constant.MaxMemberToAddFCMember){
          return memberEmails;
        }else
        {
          let fcResponse=await graphClient
          .api(GraphConstants.PeopleUrlEndpoint)
          .get();  

          let frequentColl: Person[] = fcResponse.value;
          let frEmail=frequentColl.flatMap((member) => member.scoredEmailAddresses!.map((address) => address.address!))
          return memberEmails.concat(frEmail.filter(fr=>!memberEmails.includes(fr)));  
        }
      } 
      
    } catch (error :any) {
      appInsights.trackException({ exception:{ message:error, name: `${GraphSlice.name}/${getMembersAsync.name}: Exception` }, severityLevel: SeverityLevel.Error});
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getMembersEmailGuidsMappingAsync = createAsyncThunk(
  "graph/getMembersEmailGuidsMapping",
  async (graphClient: Client, thunkAPI) => {
    try {
      var response: any = [];      
      const ctx = await thunkAPI.dispatch(getContextAsync()).unwrap();
      appInsights.trackTrace({ message:"team context", severityLevel: SeverityLevel.Information, properties: { ctx }})
      if (ctx === undefined) {
        appInsights.trackException({ exception:{ message:"undefined team context", name: `${GraphSlice.name}/${getMembersEmailGuidsMappingAsync.name}: Exception` }, severityLevel: SeverityLevel.Error});
        return thunkAPI.rejectWithValue("graph: failed to load context");
      }
      if(ctx.channel === undefined && ctx.team === undefined && ctx.chat === undefined)
      {
        response = await graphClient
        .api(GraphConstants.PeopleUrlEndpoint)
        .get();

        let persons: Person[] = response.value;
        let aadUsers : AadUserConversationMember[] = [];
        
        persons.forEach((usr) => {          
         aadUsers.push({ userId: usr.id, email: usr.scoredEmailAddresses![0].address});
        });
        return aadUsers;
      }
      else 
      {
        if (ctx.chat !== undefined)  {
          //group chat
          response = await graphClient
            .api(
              Extensions.FormatString(
                GraphConstants.GroupChatMembersUrlEndpoint,
                ctx.chat?.id!
              )
            )
            .get();
        } else if (ctx.channel !== undefined) {
          //teams channel
          response = await graphClient
            .api(
              Extensions.FormatString(
                GraphConstants.TeamMembersUrlEndpoint,
                ctx.team?.groupId!
              )
            )
            .get();
        }
        let message: AadUserConversationMember[] = response.value;
        if(message.length > Constant.MaxMemberToAddFCMember){
          return message;
        }else
        {
          let fcResponse=await graphClient
          .api(GraphConstants.PeopleUrlEndpoint)
          .get();  

          let frequentColl: Person[] = fcResponse.value;
          let newMessage: AadUserConversationMember[] = [];

          frequentColl.forEach((usr) => {          
            newMessage.push({ userId: usr.id, email: usr.scoredEmailAddresses![0].address});
          });
          return newMessage;
        }
      } 
      
    } catch (error :any) {
      appInsights.trackException({ exception:{ message:error, name: `${GraphSlice.name}/${getMembersEmailGuidsMappingAsync.name}: Exception` }, severityLevel: SeverityLevel.Error});
      return thunkAPI.rejectWithValue(error);
    }
  }
);



export interface GraphType {
  client: Client;
  members: string[];
  memberGuidMapping: AadUserConversationMember[];
  status: StateIndicator;
  membersStatus: StateIndicator;
  memberGuidStatus: StateIndicator;
}

const initialState: GraphType = {
  client: {} as Client,
  members: [] as string[],
  memberGuidMapping:{} as AadUserConversationMember[],
  status: StateIndicator.Idle,
  membersStatus: StateIndicator.Idle,
  memberGuidStatus: StateIndicator.Idle
};

const GraphSlice = createSlice({
  name: "graph",
  initialState,
  reducers: {
    resetMembersStatus(state, action){
      state.membersStatus = action.payload;
    },
    resetMembersGuisStatus(state,action){
      state.memberGuidStatus = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(initializeGraphClientAsync.rejected, (state) => {
        state.status = StateIndicator.Failed;
      })
      .addCase(initializeGraphClientAsync.pending, (state) => {
        state.status = StateIndicator.Loading;
      })
      .addCase(initializeGraphClientAsync.fulfilled, (state, action) => {
        state.status = StateIndicator.Success;
        state.client = action.payload as Client;
      })
      .addCase(getMembersAsync.rejected, (state) => {
        state.membersStatus = StateIndicator.Failed;
      })
      .addCase(getMembersAsync.pending, (state) => {
        state.membersStatus = StateIndicator.Loading;
      })
      .addCase(getMembersAsync.fulfilled, (state, action) => {
        state.membersStatus = StateIndicator.Success;
        state.members = action.payload;
      })
      .addCase(getMembersEmailGuidsMappingAsync.rejected, (state) => {
        state.memberGuidStatus = StateIndicator.Failed;
      })
      .addCase(getMembersEmailGuidsMappingAsync.pending, (state) => {
        state.memberGuidStatus = StateIndicator.Loading;
      })
      .addCase(getMembersEmailGuidsMappingAsync.fulfilled, (state, action) => {
        state.memberGuidStatus = StateIndicator.Success;
        state.memberGuidMapping = action.payload as AadUserConversationMember[];
      });
  },
});
export const getGraphState = (state: RootState) => state.graph;
export const graphActions = GraphSlice.actions;
export default GraphSlice;
